import { useState, useMemo } from "react";
import { num, toM, m2, money, UNITS, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, ResultCard, useCatalog, ProductSelect } from "./shared";

const LUX_USO = { "Sala / estar": 200, "Comedor": 250, "Cocina": 300, "Recámara": 150, "Baño": 200, "Oficina": 500, "Sala de juntas": 500, "Comercio": 500, "Pasillo": 100, "Taller / industrial": 750, "Exterior / jardín": 100 };

export default function Iluminacion({ onSave, s }) {
  const items = useCatalog("iluminacion");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("m");
  const [uso, setUso] = useState("Sala / estar");
  const [largo, setLargo] = useState("5");
  const [ancho, setAncho] = useState("4");
  const [lux, setLux] = useState("200");
  const [lumensLum, setLumensLum] = useState("1000");
  const [potencia, setPotencia] = useState("10");
  const [fu, setFu] = useState("0.60");
  const [fm, setFm] = useState("0.80");
  const [horasDia, setHorasDia] = useState("5");
  const [tarifa, setTarifa] = useState("2.50");
  const [precio, setPrecio] = useState("");
  const pr = prod ? prod.price : num(precio);

  const r = useMemo(() => {
    const area = toM(largo, u) * toM(ancho, u);
    const luxObj = num(lux) || LUX_USO[uso] || 200;
    const lm = num(lumensLum);
    const fuv = num(fu) || 0.6, fmv = num(fm) || 0.8;
    const lumensReq = area * luxObj;
    const lumensCorr = fuv * fmv > 0 ? lumensReq / (fuv * fmv) : 0;
    const cantidad = lm > 0 ? Math.ceil(lumensCorr / lm) : 0;
    const filas = cantidad > 0 ? Math.round(Math.sqrt(cantidad * (toM(ancho, u) / Math.max(0.01, toM(largo, u))))) || 1 : 0;
    const cols = filas > 0 ? Math.ceil(cantidad / filas) : 0;
    const luxReal = area > 0 ? (cantidad * lm * fuv * fmv) / area : 0;
    const potTotal = cantidad * num(potencia); // W
    const kwhDia = (potTotal / 1000) * num(horasDia);
    const kwhMes = kwhDia * 30;
    const costoMes = kwhMes * num(tarifa);
    const cMat = cantidad * pr;
    const total = cMat;
    const w = ["Estimación preliminar según método de lúmenes (flujo). Para proyectos críticos se recomienda un cálculo fotométrico profesional."];
    if (lm <= 0) w.push("Captura los lúmenes de la luminaria.");
    if (prod && prod.stock < cantidad) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock}, requieres ${cantidad} luminarias.`);
    const rows = [
      { section: "Proyecto" },
      { label: "Uso del espacio", value: uso }, { label: "Área", value: m2(area) },
      { label: "Nivel deseado", value: `${luxObj} lux` },
      { section: "Cálculo (método de lúmenes)" },
      { label: "Lúmenes requeridos", value: `${lumensReq.toLocaleString("es-MX")} lm` },
      { label: "Factor de utilización", value: fuv.toFixed(2) }, { label: "Factor de mantenimiento", value: fmv.toFixed(2) },
      { label: "Lúmenes corregidos", value: `${Math.round(lumensCorr).toLocaleString("es-MX")} lm` },
      { label: "Lúmenes por luminaria", value: `${lm.toLocaleString("es-MX")} lm` },
      { label: "Luminarias necesarias", value: `${cantidad}`, strong: true },
      { label: "Distribución sugerida", value: cantidad > 0 ? `${filas} × ${cols}` : "—" },
      { label: "Nivel real estimado", value: `${luxReal.toFixed(0)} lux` },
      { section: "Consumo energético" },
      { label: "Potencia por luminaria", value: `${num(potencia)} W` }, { label: "Potencia total", value: `${potTotal} W` },
      { label: "Consumo diario", value: `${kwhDia.toFixed(2)} kWh` }, { label: "Consumo mensual", value: `${kwhMes.toFixed(1)} kWh` },
      { label: "Costo eléctrico mensual", value: money(costoMes) },
      { section: "Complementos" },
      { label: "Drivers / fuentes", value: "Según luminaria" }, { label: "Control / dimmer", value: "Opcional" },
      { section: "Costos" },
      { label: "Luminarias", value: money(cMat) }, { label: "Total estimado", value: money(total), strong: true },
    ];
    return { rows, warnings: w, total, inputs: { u, uso } };
  }, [u, uso, largo, ancho, lux, lumensLum, potencia, fu, fm, horasDia, tarifa, pr, prod]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular iluminación?"><p>Método de lúmenes: nivel deseado (lux) × área ÷ (factor de utilización × mantenimiento) ÷ lúmenes por luminaria. Incluye distribución sugerida, nivel real y consumo/costo eléctrico. Es una estimación preliminar.</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS} testid="il-unidad" />
          <Sel label="Uso del espacio" value={uso} onChange={(v) => { setUso(v); setLux(String(LUX_USO[v] || 200)); }} opts={Object.keys(LUX_USO)} testid="il-uso" />
          <Field label="Largo del espacio" value={largo} onChange={setLargo} suffix={u} testid="il-largo" />
          <Field label="Ancho del espacio" value={ancho} onChange={setAncho} suffix={u} testid="il-ancho" />
          <Field label="Nivel deseado (lux)" value={lux} onChange={setLux} step="10" suffix="lux" testid="il-lux" />
          <Field label="Lúmenes por luminaria" value={lumensLum} onChange={setLumensLum} step="50" suffix="lm" testid="il-lumens" />
          <Field label="Potencia por luminaria (W)" value={potencia} onChange={setPotencia} step="1" suffix="W" testid="il-potencia" />
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="il-product" />
        <Advanced>
          <Field label="Factor de utilización" value={fu} onChange={setFu} step="0.05" suffix="" testid="il-fu" />
          <Field label="Factor de mantenimiento" value={fm} onChange={setFm} step="0.05" suffix="" testid="il-fm" />
          <Field label="Horas de uso / día" value={horasDia} onChange={setHorasDia} step="0.5" suffix="h" testid="il-horas" />
          <Field label="Tarifa eléctrica ($/kWh)" value={tarifa} onChange={setTarifa} step="0.1" suffix="$" testid="il-tarifa" />
          <Field label="Precio por luminaria ($)" value={prod ? prod.price : precio} onChange={setPrecio} suffix="$" testid="il-precio" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Iluminación — GOÛT" modality="iluminacion" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
